import * as React from 'react'
import { useEffect } from 'react'
import type { JSX } from 'react'
import { Link, Outlet } from 'react-router-dom'

import { clsx } from 'clsx'
import sampleSize from 'lodash/sampleSize'
import sample from 'lodash/sample'
import {
  createSerializer,
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
} from 'nuqs'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Badge,
  Building2Icon,
  Button,
  Chips,
  ChevronDownIcon,
  DropdownMenu,
  EmptyState,
  Flex,
  LockIcon,
  PageHeader,
  PageLayout,
  PencilIcon,
  PlusIcon,
  SparklesIcon,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import {
  useAccount,
  useHasEntitlement,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'
import { TemplateCard } from '~publish/components/TemplateCard'
import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { ViewOnlyPublishCoachMark } from '~publish/components/ViewOnlyCoachMarks'
import type { ViewOnlyCopy } from '~publish/components/ViewOnlyCoachMarks'
import { CustomTemplateDialog } from '~publish/components/CustomTemplateDialog'
import type {
  CustomTemplateDialogTemplate,
  CustomTemplateVisibility,
} from '~publish/components/CustomTemplateDialog'
import { useIsTemplateManager } from '~publish/components/TemplateManager'
import {
  CreatePrimaryAction,
  CreateSubnav,
  CreateTabsActions,
} from '~publish/pages/Create/CreateHeaderSlots'
import { usePageTitle } from '~publish/hooks/usePageTitle'

import { CampaignBanner } from '../../campaigns/components/CampaignBanner'
import { FeaturedTemplatesBanner } from '../../campaigns/components/FeaturedTemplatesBanner'
import { InternalPreviewDatePicker } from '../../campaigns/components/InternalPreviewDatePicker'
import {
  TemplateLibraryProvider,
  useTemplateLibrary,
} from '../../campaigns/TemplateLibraryContext'
import { isTemplateInCampaign } from '../../campaigns/utils'
import {
  isCustomTemplate,
  toCustomFromBuffer,
  toDialogTemplate,
  useDeleteCustomTemplate,
} from '../../hooks/customTemplateHelpers'
import { useCanCreatePostTemplate } from '../../hooks/useCanCreatePostTemplate'
import { useMyPostTemplates } from '../../hooks/useMyPostTemplates'
import type { CustomTemplate } from '../../hooks/useMyPostTemplates'
import { useTemplateSearch } from '../../hooks/useTemplateSearch'
import { PostTemplatesPaywallDialog } from '../PostTemplatesPaywallDialog'
import { TYPES, USER_TYPES, STRUCTURES, GOALS } from '../../types'
import type { Template, TemplateCampaign } from '../../types'
import { TemplateDialog } from '../Template/TemplateDialog'

import {
  LibraryTemplateGrid,
  NarrowMenuTitle,
  NoTemplatesFound,
  TemplateSection,
  TemplateSections,
  TemplatesLoadingGrid,
  TemplatesSearchAndFilter,
  toTemplateFilterOptions,
} from './TemplateLibraryParts'
import type { TemplateFilterGroup } from './TemplateLibraryParts'
import styles from './TemplateLibraryPage.module.css'

type LibraryView = 'discover' | 'team' | 'personal'

const isLibraryView = (value: string): value is LibraryView =>
  value === 'discover' || value === 'team' || value === 'personal'

// Single source of truth for the library-page URL state. Mirroring the
// shape in a nuqs serializer keeps card links from drifting (e.g. dropping
// the view tab) when we add a new dialog query param.
const libraryQueryParsers = {
  view: parseAsString.withDefault('discover'),
  query: parseAsString.withDefault(''),
  filters: parseAsArrayOf(parseAsString).withDefault([]),
  cta: parseAsString,
  trigger: parseAsString,
}

const serializeLibrarySearch = createSerializer(libraryQueryParsers)

type DialogState =
  | { mode: 'create'; visibility: CustomTemplateVisibility }
  | { mode: 'edit' | 'duplicate'; template: CustomTemplateDialogTemplate }
  | { mode: 'view'; template: CustomTemplate }
  | null

// ---------- Shared helpers ----------

type NoMyTemplatesYetVariant = 'personal' | 'team'

const NO_MY_TEMPLATES_COPY: Record<
  NoMyTemplatesYetVariant,
  { icon: JSX.Element; heading: string; description: string }
> = {
  personal: {
    icon: <LockIcon />,
    heading: 'Save the formats you reuse most',
    description:
      'Personal templates are private to you. Turn the posts you write again and again into one-click starting points.',
  },
  team: {
    icon: <Building2Icon />,
    heading: 'Give your team a head start',
    description:
      'Team templates are shared across your organization. Create one so everyone can post on-brand in seconds.',
  },
}

const NoMyTemplatesYet = ({
  variant,
  canCreate,
  onCreate,
}: {
  variant: NoMyTemplatesYetVariant
  canCreate: boolean
  onCreate: () => void
}): JSX.Element => {
  const copy = NO_MY_TEMPLATES_COPY[variant]

  return (
    <EmptyState size="large" variant="primary" className={styles.myEmptyState}>
      <EmptyState.Icon>{copy.icon}</EmptyState.Icon>
      <EmptyState.Heading>{copy.heading}</EmptyState.Heading>
      <EmptyState.Description>{copy.description}</EmptyState.Description>
      {canCreate && (
        <EmptyState.Actions>
          <Button variant="primary" size="medium" onClick={onCreate}>
            <PlusIcon />
            New Template
          </Button>
        </EmptyState.Actions>
      )}
    </EmptyState>
  )
}

const LoadingSkeleton = (): JSX.Element => {
  return (
    <PageLayout>
      <PageLayout.Container
        className={`${styles.feedContent} ${styles.feedLoading}`}
      >
        <TemplatesLoadingGrid />
      </PageLayout.Container>
    </PageLayout>
  )
}

// ---------- Shared building blocks ----------

type LibraryCampaignBannerProps = {
  templates: Template[]
  activeCampaigns: TemplateCampaign[]
  primaryCampaign: TemplateCampaign | undefined
  onApplyCampaignFilter: (campaignName: string) => void
  scrollTargetRef: React.RefObject<HTMLDivElement | null>
  /** When true, render nothing (e.g. on the Team/Personal tabs). */
  hidden?: boolean
}

const LibraryCampaignBanner = ({
  templates,
  activeCampaigns,
  primaryCampaign,
  onApplyCampaignFilter,
  scrollTargetRef,
  hidden = false,
}: LibraryCampaignBannerProps): JSX.Element | null => {
  const primaryCampaignTemplates = React.useMemo(() => {
    if (!primaryCampaign?.id || !primaryCampaign.templateIds?.length) {
      return []
    }
    const linked = templates.filter((t) =>
      t.linkedCampaigns.includes(primaryCampaign.id),
    )
    if (primaryCampaign.campaignType === 'Collection') {
      const random = sample(linked)
      return [random ?? linked[0]]
    }
    return linked.toSorted((a, b) => {
      const ia = primaryCampaign.templateIds.indexOf(a.id)
      const ib = primaryCampaign.templateIds.indexOf(b.id)
      if (ia !== -1 && ib !== -1) return ia - ib
      if (ia !== -1) return -1
      if (ib !== -1) return 1
      return 0
    })
  }, [templates, primaryCampaign])

  const todaysTemplate = React.useMemo(() => {
    if (!primaryCampaign || primaryCampaignTemplates.length === 0) return null
    const dayIndex = (primaryCampaign.currentDayNumber ?? 1) - 1
    const clamped = Math.min(dayIndex, primaryCampaignTemplates.length - 1)
    return primaryCampaignTemplates[clamped] ?? null
  }, [primaryCampaign, primaryCampaignTemplates])

  const featuredTemplates = React.useMemo(() => {
    const featured = templates.filter((t) => t.featured)
    return sampleSize(featured, 3)
  }, [templates])

  if (hidden) return null

  const handleApply = (): void => {
    if (!primaryCampaign) return
    onApplyCampaignFilter(primaryCampaign.name)
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    scrollTargetRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? 'instant' : 'smooth',
    })
  }

  return primaryCampaign ? (
    <PageHeader.Row className={styles.headerRow}>
      <CampaignBanner
        campaign={primaryCampaign}
        templates={
          primaryCampaign.campaignType === 'Creator Camp' && todaysTemplate
            ? [todaysTemplate]
            : primaryCampaignTemplates
        }
        onFilter={handleApply}
      />
    </PageHeader.Row>
  ) : (
    <PageHeader.Row className={styles.headerRow}>
      <FeaturedTemplatesBanner
        templates={featuredTemplates}
        activeCampaigns={activeCampaigns}
      />
    </PageHeader.Row>
  )
}

type PrimaryCampaignSectionBannerProps = {
  campaign: TemplateCampaign
  templates: Template[]
  onApplyCampaignFilter: (campaignName: string) => void
}

/**
 * Inline variant of the campaign banner that lives inside the primary
 * campaign's section row in browse mode. It picks the same "today's
 * template" / sorted templates that the header banner did so the visual
 * stays identical — only the placement changes.
 */
const PrimaryCampaignSectionBanner = ({
  campaign,
  templates,
  onApplyCampaignFilter,
}: PrimaryCampaignSectionBannerProps): JSX.Element | null => {
  const campaignTemplates = React.useMemo(() => {
    if (!campaign.id || !campaign.templateIds?.length) return []
    const linked = templates.filter((t) =>
      t.linkedCampaigns.includes(campaign.id),
    )
    if (campaign.campaignType === 'Collection') {
      const random = sample(linked)
      return random ? [random] : linked.slice(0, 1)
    }
    return linked.toSorted((a, b) => {
      const ia = campaign.templateIds.indexOf(a.id)
      const ib = campaign.templateIds.indexOf(b.id)
      if (ia !== -1 && ib !== -1) return ia - ib
      if (ia !== -1) return -1
      if (ib !== -1) return 1
      return 0
    })
  }, [templates, campaign])

  const todaysTemplate = React.useMemo(() => {
    if (campaignTemplates.length === 0) return null
    const dayIndex = (campaign.currentDayNumber ?? 1) - 1
    const clamped = Math.min(dayIndex, campaignTemplates.length - 1)
    return campaignTemplates[clamped] ?? null
  }, [campaign, campaignTemplates])

  if (campaignTemplates.length === 0) return null

  return (
    <CampaignBanner
      campaign={campaign}
      templates={
        campaign.campaignType === 'Creator Camp' && todaysTemplate
          ? [todaysTemplate]
          : campaignTemplates
      }
      onFilter={() => onApplyCampaignFilter(campaign.name)}
      titleAs="h3"
    />
  )
}

// ---------- Narrow-viewport controls ----------

/**
 * DropdownMenu switches to a Drawer below 640px; the popover variant used
 * above that has no Dialog context, so an unconditional `Drawer.Title` throws
 * `DialogTitle must be used within Dialog`. Render the a11y title only when
 * we're actually in the drawer variant.
 */
type ViewOption = { value: LibraryView; label: string; count: number }

type ViewChipsNarrowProps = {
  options: ViewOption[]
  value: LibraryView
  onChange: (next: LibraryView) => void
  className?: string
}

/**
 * On narrow headers the three view chips collapse into a single dropdown
 * showing the active view's label. Keeps the active count badge visible so
 * users still see how many templates are in the current view.
 */
const ViewChipsNarrow = ({
  options,
  value,
  onChange,
  className,
}: ViewChipsNarrowProps): JSX.Element => {
  const active = options.find((o) => o.value === value) ?? options[0]

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <Button
          variant="tertiary"
          size="small"
          className={clsx(styles.viewChip, className)}
        >
          {active.label}
          <Badge
            size="small"
            className={clsx(styles.viewChipBadge, styles.viewChipBadgeActive)}
          >
            {active.count}
          </Badge>
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        <NarrowMenuTitle>Switch view</NarrowMenuTitle>
        {options.map((option) => (
          <DropdownMenu.Item
            key={option.value}
            onSelect={() => onChange(option.value)}
          >
            {option.label}
            <Badge
              size="small"
              className={clsx(
                styles.viewChipBadge,
                option.value === value && styles.viewChipBadgeActive,
              )}
            >
              {option.count}
            </Badge>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}

const VIEW_ONLY_TEMPLATES_COPY: ViewOnlyCopy = {
  title: 'You have view-only access to Templates',
  body:
    'You can browse and use the templates. To create, edit, or delete ' +
    'templates, ask an admin to update your permissions.',
}

const canEditTemplate = (t: CustomTemplate): boolean =>
  t.allowedActions.includes('edit')
const canDeleteTemplate = (t: CustomTemplate): boolean =>
  t.allowedActions.includes('delete')

const TemplateLibraryContent = (): JSX.Element => {
  const themedTemplatesInternalDateSelectorEnabled = useIsSplitEnabled(
    'themed-templates-internal-calendar',
  )
  const { canManage, isReady: isManagerReady } = useIsTemplateManager()
  const canManageTemplates = useHasOrganizationAction('manageTemplates')

  const contentRef = React.useRef<HTMLDivElement>(null)

  const {
    loading,
    activeCampaigns,
    primaryCampaign,
    visibleTemplates: templates,
    getTemplateCampaignTags,
  } = useTemplateLibrary()

  const [filters, setFilters] = useQueryStates(libraryQueryParsers, {
    history: 'push',
  })

  // Team templates are a team-plan feature (`teamsPanel` covers Team and
  // Agency); without it the Team view is hidden and a `?view=team` link
  // falls back to Discover.
  const hasTeamFeatures = useHasEntitlement('teamsPanel')
  const { loading: accountLoading } = useAccount()

  // Normalize so an unknown `?view=foo` (or a team link opened without team
  // features) doesn't bleed into downstream branching — defaults to discover.
  const view: LibraryView =
    isLibraryView(filters.view) && (filters.view !== 'team' || hasTeamFeatures)
      ? filters.view
      : 'discover'
  const query = filters.query
  const activeFilters = filters.filters
  const campaignFilterGroups: TemplateFilterGroup[] =
    activeCampaigns.length > 0
      ? [
          {
            heading: 'Campaigns',
            filterType: 'campaign',
            options: activeCampaigns.map((campaign) => ({
              value: campaign.name,
              label: campaign.title ?? campaign.name,
              chipLabel: (
                <>
                  <SparklesIcon />
                  {campaign.title ?? campaign.name}
                </>
              ),
            })),
          },
        ]
      : []
  const filterGroups: TemplateFilterGroup[] = [
    ...campaignFilterGroups,
    {
      heading: 'Type',
      filterType: 'category',
      options: toTemplateFilterOptions(TYPES),
    },
    {
      heading: 'Made for',
      filterType: 'madeFor',
      options: toTemplateFilterOptions(USER_TYPES),
    },
    {
      heading: 'Format',
      filterType: 'format',
      options: toTemplateFilterOptions(STRUCTURES),
    },
    {
      heading: 'Goal',
      filterType: 'goal',
      options: toTemplateFilterOptions(GOALS),
    },
  ]

  // Build the details-link search from nuqs state directly. We can't read
  // `useLocation().search` here because nuqs writes shallow by default
  // (history.pushState bypasses react-router), so the location can lag the
  // real URL right after a tab switch — and that's exactly when the user
  // clicks a card.
  const buildDetailsSearch = (templateId: string): string =>
    serializeLibrarySearch({
      view,
      query: filters.query,
      filters: filters.filters,
      cta: 'create-templates-browse-list-1',
      trigger: `template-card-templatesPage-${templateId}`,
    })

  const {
    templates: myCustomTemplates,
    loading: myLoading,
    hasMorePersonal,
    hasMoreTeam,
    loadMorePersonal,
    loadMoreTeam,
    loadingMorePersonal,
    loadingMoreTeam,
  } = useMyPostTemplates()

  const { deleteTemplate } = useDeleteCustomTemplate()

  // Ownership-based splits power the chip badges and per-view lists.
  const bufferTemplates = templates
  const teamTemplates = React.useMemo(
    () => myCustomTemplates.filter((t) => t.owner === 'team'),
    [myCustomTemplates],
  )
  const personalTemplates = React.useMemo(
    () => myCustomTemplates.filter((t) => t.owner === 'mine'),
    [myCustomTemplates],
  )

  // Base (unfiltered) template list for the selected ownership view.
  const baseTemplates = React.useMemo(() => {
    switch (view) {
      case 'team':
        return teamTemplates
      case 'personal':
        return personalTemplates
      case 'discover':
      default:
        return bufferTemplates
    }
  }, [view, bufferTemplates, teamTemplates, personalTemplates])

  // Filter controls only render on Discover. Honoring `?filters=…` on
  // Team/Personal would silently apply hidden state with no way to inspect
  // or clear it — the URL normalization below strips it out of state too.
  const effectiveFilters = view === 'discover' ? activeFilters : []

  const filteredTemplates = useTemplateSearch({
    templates: baseTemplates,
    query,
    filters: effectiveFilters,
    activeCampaigns,
  })

  // Discover's default "browse" state: no search and no active filters. There
  // we group the library into category sections; any active filter/search
  // collapses back to a flat grid. Team/Personal always render a flat grid.
  const isBrowsing = view === 'discover' && !query && activeFilters.length === 0

  // The primary campaign banner is contextual: it advertises that one
  // campaign. Once the user filters into something else (e.g. `?filters=Tip`)
  // the banner is misleading, so suppress it unless the only active filter is
  // the primary campaign itself. In browse mode we inline the banner inside
  // its own section row, so the header copy is also hidden.
  const filterIsPrimaryCampaign =
    primaryCampaign != null &&
    activeFilters.length === 1 &&
    activeFilters[0] === primaryCampaign.name
  const hideHeaderCampaignBanner =
    view !== 'discover' ||
    query.length > 0 ||
    (primaryCampaign != null && isBrowsing) ||
    (activeFilters.length > 0 && !filterIsPrimaryCampaign)

  // Category sections for the Discover browse view (campaigns + types). Team
  // and Personal are flat lists with no categories, so they don't section.
  const sections = React.useMemo(() => {
    const result: {
      key: string
      title: string
      templates: Template[]
      onSeeAll: () => void
    }[] = []

    activeCampaigns.forEach((campaign) => {
      const inCampaign = baseTemplates.filter((t) =>
        isTemplateInCampaign(t, campaign),
      )
      if (inCampaign.length > 0) {
        result.push({
          key: `campaign-${campaign.id}`,
          title: campaign.title ?? campaign.name,
          templates: inCampaign,
          onSeeAll: () => void setFilters({ filters: [campaign.name] }),
        })
      }
    })

    TYPES.forEach((type) => {
      const ofType = baseTemplates.filter((t) => t.type === type)
      if (ofType.length > 0) {
        result.push({
          key: `type-${type}`,
          title: type,
          templates: ofType,
          onSeeAll: () => void setFilters({ filters: [type] }),
        })
      }
    })

    return result
  }, [baseTemplates, activeCampaigns, setFilters])

  // Sections own an `h2` each, so the cards inside them are `h3`. The flat grid
  // has no section heading, so its cards are the first level under the page.
  const showsSections = isBrowsing && sections.length > 0

  // Keep `?filters=…` honest: outside Discover the chips aren't rendered, and
  // inside Discover we drop unknown values so a stale link doesn't surface
  // filters the UI no longer offers.
  useEffect(() => {
    if (view !== 'discover') {
      if (activeFilters.length > 0) {
        void setFilters({ filters: [] })
      }
      return
    }
    if (activeFilters.length === 0) return
    const validFilters = new Set<string>([
      ...activeCampaigns.map((c) => c.name),
      ...TYPES,
      ...USER_TYPES,
      ...STRUCTURES,
      ...GOALS,
    ])
    const normalized = activeFilters.filter((f) => validFilters.has(f))
    if (normalized.length !== activeFilters.length) {
      void setFilters({ filters: normalized })
    }
  }, [view, activeCampaigns, activeFilters, setFilters])

  // A blocked `?view=team` link renders Discover (see the `view` fallback
  // above); also write the URL back so the query state matches what's shown.
  // Waits for the account to load — entitlements read as absent while the
  // account query is in flight, and rewriting early would clobber a valid
  // team link for an entitled user. Replaces instead of pushing (the hook
  // default) so canonicalizing doesn't add a history entry — pushing would
  // trap Back on the team URL, which immediately rewrites again.
  useEffect(() => {
    if (accountLoading || hasTeamFeatures) return
    if (filters.view === 'team') {
      void setFilters({ view: 'discover' }, { history: 'replace' })
    }
  }, [accountLoading, hasTeamFeatures, filters.view, setFilters])

  const resetFilters = (): void => {
    void setFilters({ filters: [], query: '' })
  }

  const handleViewChange = (next: string): void => {
    void setFilters({ view: next, filters: [], query: '' })
  }

  const allViewOptions: ViewOption[] = [
    { value: 'discover', label: 'Discover', count: bufferTemplates.length },
    { value: 'team', label: 'Team', count: teamTemplates.length },
    { value: 'personal', label: 'Personal', count: personalTemplates.length },
  ]
  const viewOptions = hasTeamFeatures
    ? allViewOptions
    : allViewOptions.filter((option) => option.value !== 'team')

  const [dialogState, setDialogState] = React.useState<DialogState>(null)
  const [paywallOpen, setPaywallOpen] = React.useState(false)

  const {
    canCreate,
    isMpCustomer,
    isLoading: templateLimitsLoading,
  } = useCanCreatePostTemplate()

  // All create paths (new + duplicate) go through one gate so server-side
  // limit enforcement (PR #20904) is mirrored in the UI: at-limit → paywall,
  // below-limit → existing dialog. MP customers are short-circuited before the
  // paywall — post templates aren't part of their plan and the paywall's
  // upgrade path doesn't apply to them. Their entry-point CTAs are disabled
  // below; these guards are the last-line safety net for secondary paths (the
  // duplicate action on template cards, the empty-state CTA).
  const openCreate = (): void => {
    if (isMpCustomer) return
    if (!canManageTemplates) return
    if (!canCreate) {
      setPaywallOpen(true)
      return
    }
    // Seed visibility from the active tab: creating from Team defaults to the
    // org-shared option, everywhere else to private.
    const visibility: CustomTemplateVisibility =
      view === 'team' ? 'internal' : 'private'
    setDialogState({ mode: 'create', visibility })
  }
  const openView = (t: CustomTemplate): void =>
    setDialogState({ mode: 'view', template: t })
  const openEdit = (t: CustomTemplate): void =>
    setDialogState({ mode: 'edit', template: toDialogTemplate(t) })
  const openDuplicate = (t: CustomTemplate): void => {
    if (isMpCustomer) return
    if (!canManageTemplates) return
    if (!canCreate) {
      setPaywallOpen(true)
      return
    }
    setDialogState({ mode: 'duplicate', template: toDialogTemplate(t) })
  }
  const closeDialog = (open: boolean): void => {
    if (!open) setDialogState(null)
  }

  const renderTemplateCard = (template: Template): JSX.Element => {
    const custom = isCustomTemplate(template) ? template : null
    const ownerTag = custom ? custom.owner : 'buffer'
    return (
      <TemplateCard
        key={template.id}
        template={{ ...template, owner: ownerTag }}
      >
        <TemplateCard.Clickable
          asChild
          id={`template-card-templatesPage-${template.id}`}
        >
          {custom ? (
            <button
              type="button"
              aria-label={`Open template ${template.title}`}
              onClick={() => openView(custom)}
            />
          ) : (
            <Link
              to={{
                pathname: `/create/templates/template/${template.id}`,
                search: buildDetailsSearch(template.id),
              }}
              aria-label={`Open template ${template.title}`}
            />
          )}
        </TemplateCard.Clickable>
        {custom ? (
          <TemplateCard.Actions
            canEdit={canManageTemplates && canEditTemplate(custom)}
            canDelete={canManageTemplates && canDeleteTemplate(custom)}
            canDuplicate={canManageTemplates}
            onEdit={() => openEdit(custom)}
            onDuplicate={() => openDuplicate(custom)}
            onDelete={() => {
              void deleteTemplate(custom.id)
            }}
          />
        ) : (
          <TemplateCard.Actions
            canEdit={false}
            canDelete={false}
            canDuplicate={canManageTemplates}
            onDuplicate={() => openDuplicate(toCustomFromBuffer(template))}
          />
        )}
        <TemplateCard.Icon />
        <TemplateCard.Title as={showsSections ? 'h3' : 'h2'} />
        <TemplateCard.Description />
        <TemplateCard.Tags customTags={getTemplateCampaignTags(template)} />
        <TemplateCard.Ribbon />
      </TemplateCard>
    )
  }

  // accountLoading gates the permission-dependent header: canManageTemplates
  // reads false while the organization is unavailable, which would flash the
  // View only badge at users who can manage templates.
  if (loading || myLoading || templateLimitsLoading || accountLoading) {
    return <LoadingSkeleton />
  }

  return (
    <>
      <Outlet />

      <CreatePrimaryAction>
        {!canManageTemplates ? (
          <ViewOnlyPublishCoachMark copy={VIEW_ONLY_TEMPLATES_COPY}>
            <ViewOnlyBadge />
          </ViewOnlyPublishCoachMark>
        ) : isMpCustomer ? (
          <Tooltip content="Post templates aren't part of your plan.">
            {/* Wrapping span — Radix Tooltip needs a focusable target, but a
                disabled `<button>` receives neither focus nor pointer events, so
                we make the span itself focusable. Keyboard users can Tab onto it
                and read the explanation. */}
            <span
              // Tooltip needs a keyboard-focusable proxy because disabled
              // buttons cannot receive focus or pointer events.
              // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
              tabIndex={0}
            >
              <Button variant="secondary" size="medium" disabled>
                <PlusIcon />
                New Template
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button variant="secondary" size="medium" onClick={openCreate}>
            <PlusIcon />
            New Template
          </Button>
        )}
      </CreatePrimaryAction>

      <CreateTabsActions>
        <TemplatesSearchAndFilter
          query={query}
          filters={activeFilters}
          groups={filterGroups}
          showFilters={view === 'discover'}
          cta="publish-templates-header-filter-1"
          onQueryChange={(next) => void setFilters({ query: next })}
          onFiltersChange={(next) => void setFilters({ filters: next })}
          onReset={resetFilters}
        />
        {isManagerReady && canManage && (
          <Button
            variant="tertiary"
            size="medium"
            as={Link}
            to="/create/templates/manage"
            className={styles.hideBelowLg}
          >
            <PencilIcon />
            Manage
          </Button>
        )}
      </CreateTabsActions>

      <CreateSubnav>
        <Chips
          type="single"
          aria-label="Template views"
          value={view}
          onChange={handleViewChange}
          required
          className={clsx(styles.viewChips, styles.hideBelowLg)}
        >
          {viewOptions.map((option) => (
            <Chips.Item key={option.value} value={option.value}>
              {option.label}
              <Badge
                size="small"
                className={clsx(
                  styles.viewChipBadge,
                  view === option.value && styles.viewChipBadgeActive,
                )}
              >
                {option.count}
              </Badge>
            </Chips.Item>
          ))}
        </Chips>
        <ViewChipsNarrow
          className={clsx(styles.viewChips, styles.hideAboveLg)}
          options={viewOptions}
          value={view}
          onChange={handleViewChange}
        />
        {/* Sits at the row's far edge — the view chips carry margin-right:auto. */}
        {themedTemplatesInternalDateSelectorEnabled && (
          <InternalPreviewDatePicker />
        )}
      </CreateSubnav>

      <PageLayout>
        <PageLayout.Container ref={contentRef} className={styles.feedContent}>
          <LibraryCampaignBanner
            templates={templates}
            activeCampaigns={activeCampaigns}
            primaryCampaign={primaryCampaign}
            onApplyCampaignFilter={(name) =>
              void setFilters({ filters: [name] })
            }
            scrollTargetRef={contentRef}
            hidden={hideHeaderCampaignBanner}
          />

          {showsSections ? (
            <TemplateSections>
              {sections.map((section) => {
                const isPrimaryCampaignSection =
                  primaryCampaign != null &&
                  section.key === `campaign-${primaryCampaign.id}`
                return (
                  <TemplateSection
                    key={section.key}
                    title={section.title}
                    templates={section.templates}
                    onSeeAll={section.onSeeAll}
                    banner={
                      isPrimaryCampaignSection ? (
                        <PrimaryCampaignSectionBanner
                          campaign={primaryCampaign}
                          templates={templates}
                          onApplyCampaignFilter={(name) =>
                            void setFilters({ filters: [name] })
                          }
                        />
                      ) : null
                    }
                  >
                    {renderTemplateCard}
                  </TemplateSection>
                )
              })}
            </TemplateSections>
          ) : view === 'personal' &&
            personalTemplates.length === 0 &&
            !query &&
            activeFilters.length === 0 ? (
            <NoMyTemplatesYet
              variant="personal"
              canCreate={canManageTemplates}
              onCreate={openCreate}
            />
          ) : view === 'team' &&
            teamTemplates.length === 0 &&
            !query &&
            activeFilters.length === 0 ? (
            <NoMyTemplatesYet
              variant="team"
              canCreate={canManageTemplates}
              onCreate={openCreate}
            />
          ) : (
            <LibraryTemplateGrid
              templates={filteredTemplates}
              emptyState={<NoTemplatesFound clearFilters={resetFilters} />}
            >
              {renderTemplateCard}
            </LibraryTemplateGrid>
          )}
          {view === 'personal' && hasMorePersonal && (
            <Flex justify="center" className={styles.loadMoreRow}>
              <Button
                variant="secondary"
                onClick={loadMorePersonal}
                disabled={loadingMorePersonal}
                aria-busy={loadingMorePersonal}
              >
                {loadingMorePersonal ? 'Loading…' : 'Load more'}
              </Button>
            </Flex>
          )}
          {view === 'team' && hasMoreTeam && (
            <Flex justify="center" className={styles.loadMoreRow}>
              <Button
                variant="secondary"
                onClick={loadMoreTeam}
                disabled={loadingMoreTeam}
                aria-busy={loadingMoreTeam}
              >
                {loadingMoreTeam ? 'Loading…' : 'Load more'}
              </Button>
            </Flex>
          )}
        </PageLayout.Container>
      </PageLayout>

      {dialogState?.mode === 'view' ? (
        <TemplateDialog
          template={dialogState.template}
          cta="create-templates-customTemplate-view-1"
          triggerId={`template-card-templatesPage-${dialogState.template.id}`}
          open
          onOpenChange={closeDialog}
          canEdit={canEditTemplate(dialogState.template)}
          onEdit={() => openEdit(dialogState.template)}
          isCustom
          byline={
            <Text color="subtle">
              {dialogState.template.owner === 'team' ? 'Your team' : 'Only you'}
            </Text>
          }
        />
      ) : dialogState ? (
        <CustomTemplateDialog
          mode={dialogState.mode}
          template={
            dialogState.mode === 'edit' || dialogState.mode === 'duplicate'
              ? dialogState.template
              : undefined
          }
          initialValues={
            dialogState.mode === 'create'
              ? { visibility: dialogState.visibility }
              : undefined
          }
          open={dialogState !== null}
          onOpenChange={closeDialog}
        />
      ) : null}

      <PostTemplatesPaywallDialog
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        upgradePathName="post-templates-limit"
        ctaProperties={{
          view: 'templatesPage',
          location: 'newTemplate',
          button: 'upgrade',
        }}
      />
    </>
  )
}

/**
 * Main page component that provides context.
 */
export const TemplateLibraryPage = (): JSX.Element => {
  usePageTitle('Templates')

  return (
    <TemplateLibraryProvider>
      <TemplateLibraryContent />
    </TemplateLibraryProvider>
  )
}
